import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Package,
  ShoppingCart,
  DollarSign,
  MessageSquare,
  TrendingUp,
  ArrowRight,
  Loader2,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeCatalog = async () => {
    setInitializing(true);
    try {
      const response = await axios.post(`${API}/admin/discovery/initialize`);
      toast.success(`Catalogo inizializzato: ${response.data.products_imported} prodotti`);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'inizializzazione');
    } finally {
      setInitializing(false);
    }
  };

  const statCards = [
    {
      title: 'Prodotti Attivi',
      value: stats?.active_products || 0,
      icon: Package,
      color: 'text-blue-600',
      bg: 'bg-blue-100',
      link: '/admin/products'
    },
    {
      title: 'Ordini Totali',
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      color: 'text-green-600',
      bg: 'bg-green-100',
      link: '/admin/orders'
    },
    {
      title: 'Fatturato',
      value: `€${(stats?.total_revenue || 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'text-amber-600',
      bg: 'bg-amber-100',
      link: '/admin/orders'
    },
    {
      title: 'Margine',
      value: `€${(stats?.total_margin || 0).toFixed(2)}`,
      icon: TrendingUp,
      color: 'text-emerald-600',
      bg: 'bg-emerald-100',
      link: '/admin/pricing'
    },
    {
      title: 'Ticket Aperti',
      value: stats?.open_tickets || 0,
      icon: MessageSquare,
      color: 'text-red-600',
      bg: 'bg-red-100',
      link: '/admin/tickets'
    },
    {
      title: 'Ordini in Attesa',
      value: stats?.pending_orders || 0,
      icon: ShoppingCart,
      color: 'text-orange-600',
      bg: 'bg-orange-100',
      link: '/admin/orders?status=new'
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-serif">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Benvenuto nel pannello di controllo QANTRI</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <Link key={index} to={stat.link}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                    <p className="text-3xl font-bold mt-2">{stat.value}</p>
                  </div>
                  <div className={`w-14 h-14 rounded-2xl ${stat.bg} flex items-center justify-center`}>
                    <stat.icon className={`h-7 w-7 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-bold font-serif mb-4">Azioni Rapide</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {stats?.active_products === 0 && (
            <Button 
              onClick={handleInitializeCatalog}
              disabled={initializing}
              className="h-auto py-4 flex-col gap-2 bg-primary"
              data-testid="initialize-catalog-btn"
            >
              {initializing ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <Sparkles className="h-6 w-6" />
              )}
              <span>{initializing ? 'Caricamento...' : 'Carica Catalogo'}</span>
            </Button>
          )}
          <Link to="/admin/discovery">
            <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
              <Sparkles className="h-6 w-6" />
              <span>Discovery</span>
            </Button>
          </Link>
          <Link to="/admin/orders?status=new">
            <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
              <ShoppingCart className="h-6 w-6" />
              <span>Gestisci Ordini</span>
            </Button>
          </Link>
          <Link to="/admin/tickets?status=open">
            <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
              <MessageSquare className="h-6 w-6" />
              <span>Rispondi Ticket</span>
            </Button>
          </Link>
          <Link to="/admin/pricing">
            <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
              <DollarSign className="h-6 w-6" />
              <span>Imposta Prezzi</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Come Iniziare</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">1</span>
              <p className="text-sm text-muted-foreground">Aggiungi un fornitore nella sezione "Fornitori"</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">2</span>
              <p className="text-sm text-muted-foreground">Importa prodotti da CSV/XML nella sezione "Importa"</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">3</span>
              <p className="text-sm text-muted-foreground">Genera contenuti AI per i prodotti nella sezione "Prodotti"</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">4</span>
              <p className="text-sm text-muted-foreground">I clienti possono ora acquistare sul sito pubblico!</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sistema Prezzi Automatico</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              QANTRI calcola automaticamente i prezzi di vendita basandosi sul costo fornitore:
            </p>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Margine Minimo:</span>
                <span className="font-bold">10%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Margine Target:</span>
                <span className="font-bold">45%</span>
              </div>
            </div>
            <Link to="/admin/pricing">
              <Button variant="outline" className="w-full">
                Modifica Impostazioni
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
